import { StateCreator } from 'zustand';
import { QuotationStore } from '../quotationStore.types';
import {
    Brand,
    CatalogItem,
    BrandPricing,
    GlobalSettings
} from '@/types/quotation';
import { quotationService } from '@/services/quotationService';
import { catalogService, CatalogItemDB } from '@/services/catalogService';
import { toast } from 'sonner';
import { normalizeTextValue } from '@/utils/textNormalization';

// Shape of items as stored in prebuiltCatalog (mapped from DB)
export interface CatalogueStoreItem {
    id: string;
    category: string;
    description: string;
    specifications?: string;
    defaultRate: number;
    defaultUom: string;
    hasDimensions: boolean;
    catalog_item_id?: string;
    industry_id?: string;
}

// Backward-compat alias used by AddItemDialog and other components
export type PrebuiltItem = CatalogueStoreItem;

export interface CatalogSlice {
    /** Live catalogue items mapped from DB — the source of truth */
    prebuiltCatalog: CatalogueStoreItem[];
    brands: Brand[];
    masterCatalog: CatalogItem[];
    brandPricing: BrandPricing[];
    globalSettings: GlobalSettings[];
    fetchCatalog: (forceFetch?: boolean, industryId?: string | null) => Promise<void>;
    addPrebuiltItem: (item: Omit<CatalogueStoreItem, 'id'> & {
        id?: string;
        sectionWorkType?: string;
        sectionName?: string;
    }) => Promise<void>;
    fetchInitialData: () => Promise<void>;
}

const normalizeText = (value: string): string =>
    value.trim().toLowerCase().replace(/\s+/g, ' ');

const CATEGORY_SYNONYMS: Record<string, string> = {
    'modular kitchen': 'kitchen',
    'kitchen units': 'kitchen',
    kitchens: 'kitchen',
    wardrobes: 'wardrobe',
    closet: 'wardrobe',
    carpentry: 'furniture',
    electricals: 'electrical',
    'plumbing works': 'plumbing',
    'civil works': 'civil',
};

const TERM_SYNONYMS: Array<[RegExp, string]> = [
    [/\bward[\s-]*robe\b/g, 'wardrobe'],
    [/\bcloset\b/g, 'wardrobe'],
    [/\bmodular[\s-]*kitchen\b/g, 'kitchen'],
    [/\bkitchen[\s-]*unit(s)?\b/g, 'kitchen unit'],
    [/\bfalse[\s-]*ceiling\b/g, 'ceiling'],
    [/\bgypsum[\s-]*board\b/g, 'gypsum'],
    [/\bdoor[\s-]*shutter\b/g, 'shutter'],
    [/\bsliding[\s-]*door\b/g, 'sliding door'],
];

const STOP_WORDS = new Set(['the', 'and', 'for', 'with', 'in', 'of', 'unit', 'units', 'item']);

const canonicalCategory = (value: string): string => {
    const normalized = normalizeText(value || 'general');
    return CATEGORY_SYNONYMS[normalized] || normalized;
};

const canonicalizeForMatch = (value: string): string => {
    let text = normalizeText(value)
        .replace(/[^a-z0-9\s]/g, ' ')
        .replace(/\s+/g, ' ');
    TERM_SYNONYMS.forEach(([regex, replacement]) => {
        text = text.replace(regex, replacement);
    });
    return text.trim();
};

const toTokenSet = (value: string): Set<string> => {
    return new Set(
        canonicalizeForMatch(value)
            .split(' ')
            .filter((t) => t.length > 1 && !STOP_WORDS.has(t))
    );
};

const levenshteinDistance = (a: string, b: string): number => {
    if (a === b) return 0;
    if (!a.length) return b.length;
    if (!b.length) return a.length;

    const dp = Array.from({ length: a.length + 1 }, () => new Array<number>(b.length + 1).fill(0));
    for (let i = 0; i <= a.length; i += 1) dp[i][0] = i;
    for (let j = 0; j <= b.length; j += 1) dp[0][j] = j;

    for (let i = 1; i <= a.length; i += 1) {
        for (let j = 1; j <= b.length; j += 1) {
            const cost = a[i - 1] === b[j - 1] ? 0 : 1;
            dp[i][j] = Math.min(
                dp[i - 1][j] + 1,
                dp[i][j - 1] + 1,
                dp[i - 1][j - 1] + cost
            );
        }
    }
    return dp[a.length][b.length];
};

const tokenOverlap = (a: Set<string>, b: Set<string>): number => {
    if (a.size === 0 || b.size === 0) return 0;
    let intersection = 0;
    a.forEach((token) => {
        if (b.has(token)) intersection += 1;
    });
    return intersection / Math.max(a.size, b.size);
};

const areLikelyDuplicateNames = (left: string, right: string): boolean => {
    const leftCanonical = canonicalizeForMatch(left);
    const rightCanonical = canonicalizeForMatch(right);
    if (!leftCanonical || !rightCanonical) return false;
    if (leftCanonical === rightCanonical) return true;

    const leftTokens = toTokenSet(leftCanonical);
    const rightTokens = toTokenSet(rightCanonical);
    const overlap = tokenOverlap(leftTokens, rightTokens);
    if (overlap >= 0.8) return true;

    const distance = levenshteinDistance(leftCanonical, rightCanonical);
    const longest = Math.max(leftCanonical.length, rightCanonical.length);
    const similarity = 1 - distance / longest;
    return similarity >= 0.88 || (longest <= 16 && distance <= 2);
};

const inferCategoryFromText = (description: string, specifications?: string, sectionWorkType?: string): string => {
    if (sectionWorkType && sectionWorkType !== 'standard') return canonicalCategory(sectionWorkType);

    const text = `${description} ${specifications || ''}`.toLowerCase();
    const keywordMap: Array<{ category: string; keywords: string[] }> = [
        { category: 'kitchen', keywords: ['kitchen', 'countertop', 'hob', 'chimney', 'backsplash'] },
        { category: 'wardrobe', keywords: ['wardrobe', 'closet', 'shutter', 'loft'] },
        { category: 'electrical', keywords: ['light', 'switch', 'db', 'wiring', 'fan'] },
        { category: 'plumbing', keywords: ['plumbing', 'tap', 'sink', 'wc', 'sanitary'] },
        { category: 'civil', keywords: ['civil', 'masonry', 'tiling', 'false ceiling', 'paint', 'gypsum'] },
        { category: 'furniture', keywords: ['table', 'bed', 'sofa', 'chair', 'cabinet'] },
    ];

    const match = keywordMap.find((entry) => entry.keywords.some((k) => text.includes(k)));
    return canonicalCategory(match?.category || 'general');
};

const inferRoomType = (sectionName?: string): string => {
    const name = (sectionName || '').trim();
    if (!name) return 'General';
    return name.length > 64 ? name.slice(0, 64) : name;
};

const dedupeCatalogItems = (items: CatalogItemDB[]): {
    unique: CatalogItemDB[];
    duplicateIds: string[];
    merges: Array<{ primary: CatalogItemDB; secondary: CatalogItemDB }>;
} => {
    const active = items.filter((i) => i.is_active !== false);
    const sorted = [...active].sort((a, b) => {
        const aCustom = a.is_custom ? 1 : 0;
        const bCustom = b.is_custom ? 1 : 0;
        if (aCustom !== bCustom) return bCustom - aCustom;
        return (new Date(b.updated_at).getTime()) - (new Date(a.updated_at).getTime());
    });

    const unique: CatalogItemDB[] = [];
    const duplicateIds = new Set<string>();
    const merges: Array<{ primary: CatalogItemDB; secondary: CatalogItemDB }> = [];

    sorted.forEach((candidate) => {
        const category = canonicalCategory(candidate.category || 'general');
        const existing = unique.find((u) =>
            canonicalCategory(u.category || 'general') === category &&
            areLikelyDuplicateNames(u.name, candidate.name)
        );

        if (!existing) {
            unique.push(candidate);
            return;
        }

        duplicateIds.add(candidate.id);
        merges.push({ primary: existing, secondary: candidate });
    });

    return { unique, duplicateIds: Array.from(duplicateIds), merges };
};

export const createCatalogSlice: StateCreator<
    QuotationStore,
    [],
    [],
    CatalogSlice
> = (set, get) => ({
    // Start empty — fetchCatalog populates from DB, not from a static array
    prebuiltCatalog: [],
    brands: [],
    masterCatalog: [],
    brandPricing: [],
    globalSettings: [],

    fetchCatalog: async (forceFetch = true, industryId?: string | null) => {
        set({ isLoading: true });
        try {
            // Single call — reuse dbItems for both prebuilt and master catalog
            // industryId=undefined → admin full view; industryId=string → project-specific
            const [dbItems, settings] = await Promise.all([
                catalogService.getItems(industryId !== undefined ? { industry_id: industryId } : undefined),
                quotationService.getGlobalSettings()
            ]);

            const dbCatalog = dbItems as unknown as CatalogItemDB[];
            const { unique, duplicateIds, merges } = dedupeCatalogItems(dbCatalog);

            // Only run deactivation/merging if we have a role that allows it and it's a force fetch
            const currentRole = String((get() as any).currentUserRole || '').toLowerCase();
            const canManageCatalog = currentRole === 'admin' || currentRole === 'owner';

            // Database-level catalog deduplication/write operations are disabled during read-only fetchCatalog to prevent RLS conflicts and unique constraint violations in tenant contexts.
            // In-memory deduplication (the 'unique' array) is still performed, ensuring clean, non-duplicate displays.
            if (false && canManageCatalog && forceFetch) {
                if (duplicateIds.length > 0) {
                    await Promise.all(duplicateIds.map((id) => catalogService.deactivateItem(id)));
                }

                if (merges.length > 0) {
                    await Promise.all(merges.map(({ primary, secondary }) => {
                        const minRate = Math.min(
                            primary.min_rate ?? primary.default_rate,
                            secondary.min_rate ?? secondary.default_rate
                        );
                        const maxRate = Math.max(
                            primary.max_rate ?? primary.default_rate,
                            secondary.max_rate ?? secondary.default_rate
                        );
                        const mergedRate = Math.round(((primary.default_rate * 2) + secondary.default_rate) / 3);
                        return catalogService.updateItem(primary.id, {
                            default_rate: mergedRate,
                            min_rate: minRate,
                            max_rate: maxRate,
                            specifications: primary.specifications || secondary.specifications || null,
                            has_dimensions: primary.has_dimensions || secondary.has_dimensions,
                        });
                    }));
                }

                if (duplicateIds.length > 0 || merges.length > 0) {
                    const sample = merges.slice(0, 4).map(({ primary, secondary }) =>
                        `${secondary.name} -> ${primary.name}`
                    );
                    console.info('[Catalog Sync] Dedupe report', {
                        mergedItems: merges.length,
                        deactivatedItems: duplicateIds.length,
                        examples: sample,
                    });
                    toast.info(
                        `Catalog synced: merged ${merges.length} duplicate item(s), deactivated ${duplicateIds.length}.`,
                        {
                            description: sample.length
                                ? sample.join(' | ')
                                : 'No duplicate examples available.',
                        }
                    );
                }
            }

            const prebuilt = unique.map(item => ({
                id: item.id,
                category: canonicalCategory(item.category),
                description: item.name,
                specifications: item.specifications || '',
                defaultRate: item.default_rate,
                defaultUom: item.default_uom,
                hasDimensions: item.has_dimensions,
                industry_id: item.industry_id || undefined
            }));

            set({
                prebuiltCatalog: prebuilt,
                brands: [],
                masterCatalog: unique as unknown as CatalogItem[],
                brandPricing: [],
                globalSettings: settings as unknown as GlobalSettings[],
                isLoading: false
            });
        } catch (err: unknown) {
            console.error('Failed to load catalog:', err);
            set({ isLoading: false });
        }
    },

    addPrebuiltItem: async (item) => {
        try {
            const inferredCategory = inferCategoryFromText(
                item.description,
                item.specifications,
                item.sectionWorkType,
            );
            const normalizedCategory = item.category && item.category !== 'all'
                ? canonicalCategory(item.category)
                : inferredCategory;
            const normalizedDescription = canonicalizeForMatch(item.description);
            const formattedDescription = normalizeTextValue(item.description, 'description');
            const formattedSpecs = normalizeTextValue(item.specifications || '', 'specifications');
            const existing = get().prebuiltCatalog.find((existingItem) =>
                canonicalCategory(existingItem.category) === normalizedCategory &&
                areLikelyDuplicateNames(existingItem.description, normalizedDescription)
            );

            // Learn from repeated custom entries instead of creating duplicates.
            if (existing) {
                const mergedRate = Math.round(((existing.defaultRate * 2) + item.defaultRate) / 3);
                const minRate = Math.min(existing.defaultRate, item.defaultRate);
                const maxRate = Math.max(existing.defaultRate, item.defaultRate);

                await catalogService.updateItem(existing.id, {
                    default_rate: mergedRate,
                    min_rate: minRate,
                    max_rate: maxRate,
                    specifications: existing.specifications || item.specifications || null,
                    has_dimensions: existing.hasDimensions || item.hasDimensions,
                });

                set((state) => ({
                    prebuiltCatalog: state.prebuiltCatalog.map((catalogItem) =>
                        catalogItem.id === existing.id
                            ? {
                                ...catalogItem,
                                defaultRate: mergedRate,
                                specifications: catalogItem.specifications || item.specifications,
                                hasDimensions: catalogItem.hasDimensions || item.hasDimensions,
                                category: normalizedCategory,
                            }
                            : catalogItem
                    ),
                }));
                return;
            }

            const created = await catalogService.addItem({
                name: formattedDescription,
                description: formattedSpecs || null,
                specifications: formattedSpecs || null,
                category: normalizedCategory,
                room_type: inferRoomType(item.sectionName),
                default_rate: item.defaultRate,
                default_uom: item.defaultUom,
                has_dimensions: item.hasDimensions,
                min_rate: item.defaultRate,
                max_rate: item.defaultRate,
                is_active: true,
                is_custom: true
            });

            if (!created) return;

            const mappedCreated: CatalogueStoreItem = {
                id: created.id,
                category: normalizedCategory,
                description: created.name,
                specifications: created.specifications || '',
                defaultRate: created.default_rate,
                defaultUom: created.default_uom,
                hasDimensions: created.has_dimensions,
            };

            set((state) => ({
                prebuiltCatalog: state.prebuiltCatalog.some((it) => it.id === mappedCreated.id)
                    ? state.prebuiltCatalog
                    : [...state.prebuiltCatalog, mappedCreated],
                masterCatalog: state.masterCatalog.some((it) => it.id === created.id)
                    ? state.masterCatalog
                    : [...state.masterCatalog, created as unknown as CatalogItem],
            }));
        } catch (err: unknown) {
            console.error('Failed to save prebuilt item:', err);
        }
    },

    fetchInitialData: async () => {
        const { fetchQuotations, fetchCatalog } = get();
        // Run in parallel — in-flight dedup in quotationService prevents double DB hits
        // even if Index.tsx also calls fetchQuotations concurrently
        await Promise.all([
            fetchQuotations(false), // false = respect cache
            fetchCatalog(false)  // false = respect cache; no industry filter = full admin view
        ]);
    }
});
